// page.jsx (MissionsPage) - Missions page component

"use client";
import dynamic from "next/dynamic";
import React, { Fragment, useEffect, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useAuth } from "@/contexts/authContext";
import { getRewardsByUID, getUserHandles } from "@/utils/firebaseFunctions";
import { formatRelativeTime } from "@/utils/generalutils";
import {
  setNewTime,
  fetchGoldLeaderboardFromSupabase,
  fetchVideoBanner,
} from "@/utils/helpers/helperFunctions";
// import LeaderboardInMissions from "@/components/missions/leaderboard-in-missions";
import UserReferralsInMissions from "@/components/missions/user-referrals-in-missions";
import ClaimsHistory from "@/components/missions/claims-history";
import AllMissions from "@/components/missions/all-missions";
import MissionsSidebar from "@/components/missions/missions-sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/custom/ui/select";

const VideoBanner = dynamic(
  () => import("@/components/new/general/VideoBanner"),
  { ssr: false }
);
const LoadingModal = dynamic(() =>
  import("@/components/partials/header/Tools/loading-modal")
);

const MissionsPage = () => {
  const { walletAddress, user, userPoints, ipAddress, updateUserPoints } =
    useAuth();

  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState(false);

  const [state, setState] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState();
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardCurrentPage, setLeaderboardCurrentPage] = useState(1);
  const [userRewards, setUserRewards] = useState([]);
  const [userHandles, setUserHandles] = useState();
  const [videoBannerURL, setVideoBannerURL] = useState(null);
  const [videoBannerMobileURL, setVideoBannerMobileURL] = useState(null);

  useEffect(() => {
    // Fetch the video banner for the missions page
    fetchVideoBanner("missions", setVideoBannerURL, setVideoBannerMobileURL);

    // Define the interval to call setNewTime every second
    const timer = setInterval(() => setNewTime(setState), 1000);

    // Clean up the interval on component unmount
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      setIsLoadingModalOpen(true);

      if (user) {
        // console.log('Fetching user rewards and handles for user:', user);
        const rewards = await getRewardsByUID(user?.user_id);
        setUserRewards(rewards);

        const handles = await getUserHandles(user?.user_id);
        setUserHandles(handles);

        setIsLoadingModalOpen(false);
      } else {
        resetAll();
        setIsLoadingModalOpen(false);
      }
    };

    const fetchLeaderboard = async () => {
      setIsLoadingModalOpen(true);

      // Fetch the leaderboard using new Supabase API
      await fetchGoldLeaderboardFromSupabase(
        "initial",
        0,
        setLeaderboardLoading,
        setLeaderboard,
        1,
        setLeaderboardCurrentPage,
        user
      );

      setIsLoadingModalOpen(false);
    };

    if (user) {
      fetchUser();
      fetchLeaderboard();
    } else {
      resetAll();
      fetchLeaderboard();
    }
  }, [user]);

  const resetAll = () => {
    setUserHandles(null);
    setUserRewards(null);
    setUserHandles(null);
  };

  useEffect(() => {
    if (isLoadingModalOpen) {
      setLoading(true);
    }

    if (!isLoadingModalOpen) {
      setLoading(false);
    }
  }, [isLoadingModalOpen]);

  return (
    <Fragment>
      <VideoBanner>
        <SwiperSlide>
          <div
            className={`swiperSlideDiv relative w-full transition-transform duration-[600ms] ease-in-out`}
          >
            <video
              preload="auto"
              src={
                videoBannerURL ||
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_about%2Fbanner%2Fdragon_banner_v3.mp4?alt=media&token=7c2c42cc-8f9c-4d30-96c5-3858d4861522"
              }
              autoPlay
              loop
              muted
              playsInline
              className="object-cover w-full h-full hidden md:block"
            />

            <video
              preload="auto"
              src={
                videoBannerMobileURL ||
                "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_about%2Fbanner%2Fdragon_banner_v3.mp4?alt=media&token=7c2c42cc-8f9c-4d30-96c5-3858d4861522"
              }
              autoPlay
              loop
              muted
              playsInline
              className="object-cover w-full h-full md:hidden"
            />
          </div>
        </SwiperSlide>
      </VideoBanner>

      <div className="!pr-8 !pl-8 xl:container">
        {/* Mobile Dropdown (hidden on md and up) */}
        <div className="md:hidden mb-6">
          <Select value={tabIndex.toString()} onValueChange={(value) => setTabIndex(parseInt(value))}>
            <SelectTrigger className="w-full bg-metaxseed-dark-gray border border-metaxseed-gray text-white text-base font-bold rounded-lg px-4 py-3 focus:border-metaxseed-blue focus:ring-2 focus:ring-metaxseed-blue focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-metaxseed-dark-gray border border-metaxseed-gray max-h-60">
              <SelectItem value="0" className="text-white hover:bg-metaxseed-content-gray focus:bg-metaxseed-content-gray focus:text-white">
                Missions
              </SelectItem>
              <SelectItem value="1" className="text-white hover:bg-metaxseed-content-gray focus:bg-metaxseed-content-gray focus:text-white">
                Claims History
              </SelectItem>
              <SelectItem value="2" className="text-white hover:bg-metaxseed-content-gray focus:bg-metaxseed-content-gray focus:text-white">
                User Referrals
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Tabs
          className={"missionsTabs"}
          selectedIndex={tabIndex}
          onSelect={(index) => setTabIndex(index)}
        >
          {/* Desktop/Tablet Horizontal Tabs (hidden on mobile) */}
          <TabList className="hidden md:flex relative lg:z-[99]">
            <Tab>
              <div className="tab-title text-lg lg:text-xl">Missions</div>
            </Tab>
            <Tab>
              <div className="tab-title text-lg lg:text-xl">Claims History</div>
            </Tab>
            {/* <Tab>
              <div className="tab-title text-lg lg:text-xl">
                Leaderboards
              </div>
            </Tab> */}
            <Tab>
              <div className="tab-title text-lg lg:text-xl">User Referrals</div>
            </Tab>
          </TabList>

          <TabPanel className={"px-0 pt-12 pb-24"}>
            <div className="grid grid-cols-12 gap-8">
              <div className="lg:col-span-8 col-span-12">
                {/* --- Column --- */}

                <div className="flex flex-col">
                  <AllMissions
                    user={user}
                    loading={loading}
                    userHandles={userHandles}
                    ipAddress={ipAddress}
                    walletAddress={walletAddress}
                    onPointsUpdate={updateUserPoints}
                  />
                </div>
              </div>

              <div className="lg:col-span-4 col-span-12">
                {/* --- Column --- */}

                <MissionsSidebar
                  loading={loading}
                  user={user}
                  leaderboard={leaderboard}
                  setTabIndex={setTabIndex}
                  state={state}
                  userPoints={userPoints}
                  userHandles={userHandles}
                  leaderboardLoading={leaderboardLoading}
                  leaderboardTabChange={(tabIndex) => {
                    setLeaderboardCurrentPage(1);
                    fetchGoldLeaderboardFromSupabase(
                      "initial",
                      tabIndex,
                      setLeaderboardLoading,
                      setLeaderboard,
                      1,
                      setLeaderboardCurrentPage,
                      user
                    );
                  }}
                  leaderboardHandleNext={(tabIndex) => {
                    fetchGoldLeaderboardFromSupabase(
                      "next",
                      tabIndex,
                      setLeaderboardLoading,
                      setLeaderboard,
                      leaderboardCurrentPage,
                      setLeaderboardCurrentPage,
                      user
                    );
                  }}
                  leaderboardHandlePrev={(tabIndex) => {
                    fetchGoldLeaderboardFromSupabase(
                      "prev",
                      tabIndex,
                      setLeaderboardLoading,
                      setLeaderboard,
                      leaderboardCurrentPage,
                      setLeaderboardCurrentPage,
                      user
                    );
                  }}
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel className={"px-0 pt-12 pb-24"}>
            <div className="grid grid-cols-12 gap-8">
              <div className="lg:col-span-8 col-span-12">
                {/* --- Column --- */}

                <ClaimsHistory
                  user={user}
                  address={walletAddress}
                  userRewards={userRewards}
                  userPoints={userPoints}
                  formatRelativeTime={formatRelativeTime}
                />
              </div>

              <div className="lg:col-span-4 col-span-12">
                {/* --- Column --- */}

                <MissionsSidebar
                  loading={loading}
                  user={user}
                  leaderboard={leaderboard}
                  setTabIndex={setTabIndex}
                  state={state}
                  userPoints={userPoints}
                  userHandles={userHandles}
                  leaderboardLoading={leaderboardLoading}
                  leaderboardTabChange={(tabIndex) => {
                    setLeaderboardCurrentPage(1);
                    fetchGoldLeaderboardFromSupabase(
                      "initial",
                      tabIndex,
                      setLeaderboardLoading,
                      setLeaderboard,
                      1,
                      setLeaderboardCurrentPage,
                      user
                    );
                  }}
                  leaderboardHandleNext={(tabIndex) => {
                    fetchGoldLeaderboardFromSupabase(
                      "next",
                      tabIndex,
                      setLeaderboardLoading,
                      setLeaderboard,
                      leaderboardCurrentPage,
                      setLeaderboardCurrentPage,
                      user
                    );
                  }}
                  leaderboardHandlePrev={(tabIndex) => {
                    fetchGoldLeaderboardFromSupabase(
                      "prev",
                      tabIndex,
                      setLeaderboardLoading,
                      setLeaderboard,
                      leaderboardCurrentPage,
                      setLeaderboardCurrentPage,
                      user
                    );
                  }}
                />
              </div>
            </div>
          </TabPanel>
          {/* <TabPanel className={"px-0 pt-12 pb-24"}>
            <LeaderboardInMissions userPoints={userPoints} user={user} address={walletAddress} tabIndex={tabIndex} setTabIndex={setTabIndex} />
          </TabPanel> */}
          <TabPanel className={"px-0 pt-12 pb-24"}>
            <UserReferralsInMissions user={user} address={walletAddress} />
          </TabPanel>
        </Tabs>
      </div>

      {isLoadingModalOpen && (
        <LoadingModal
          isOpen={isLoadingModalOpen}
          onClose={(event) => {
            if (event) {
              setIsLoadingModalOpen(false);
            }
          }}
        />
      )}
    </Fragment>
  );
};

export default MissionsPage;
